<template>
  <a-layout class="layout">
    <a-layout-sider v-model:collapsed="collapsed" :trigger="null" collapsible>
      <div class="logo">
        <img src="@/assets/logo.png" alt="Logo" class="logo-img" />
        <h3 v-if="!collapsed">{{ systemTitle }}</h3>
      </div>
      <div class="version" v-if="!collapsed">v20260525.1815</div>
      <a-menu
        theme="dark"
        mode="inline"
        :selected-keys="selectedKeys"
        :open-keys="openKeys"
        @select="handleMenuSelect"
        @openChange="handleOpenChange"
      >
        <DynamicMenu :menus="menuList" />
      </a-menu>
    </a-layout-sider>

    <a-layout>
      <a-layout-header class="header">
        <div class="header-left">
          <menu-unfold-outlined
            v-if="collapsed"
            class="trigger"
            @click="() => (collapsed = !collapsed)"
          />
          <menu-fold-outlined
            v-else
            class="trigger"
            @click="() => (collapsed = !collapsed)"
          />
          <a-breadcrumb class="breadcrumb">
            <a-breadcrumb-item>
              <router-link to="/">首页</router-link>
            </a-breadcrumb-item>
            <a-breadcrumb-item v-if="$route.meta.title">
              {{ $route.meta.title }}
            </a-breadcrumb-item>
          </a-breadcrumb>
        </div>

        <div class="header-right">
          <a-dropdown>
            <a class="ant-dropdown-link" @click.prevent>
              <UserOutlined />
              {{ userInfo.nickname || '用户' }}
              <DownOutlined />
            </a>
            <template #overlay>
              <a-menu>
                <a-menu-item disabled>
                  <UserOutlined />
                  {{ userInfo.companyName || '公司' }}
                </a-menu-item>
                <a-menu-divider />
                <a-menu-item @click="handleLogout">
                  <LogoutOutlined />
                  退出登录
                </a-menu-item>
              </a-menu>
            </template>
          </a-dropdown>
        </div>
      </a-layout-header>

      <a-layout-content class="content">
        <div class="content-wrapper">
          <router-view />
        </div>
      </a-layout-content>
    </a-layout>
  </a-layout>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Modal } from 'ant-design-vue'
import {
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  UserOutlined,
  DownOutlined,
  LogoutOutlined
} from '@ant-design/icons-vue'
import { usePermissionStore } from '@/store/permission'
import DynamicMenu from '@/components/DynamicMenu.vue'

export default {
  name: 'Layout',
  components: {
    MenuUnfoldOutlined,
    MenuFoldOutlined,
    UserOutlined,
    DownOutlined,
    LogoutOutlined,
    DynamicMenu
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const permissionStore = usePermissionStore()
    const collapsed = ref(false)
    const userInfo = ref({})

    const selectedKeys = computed(() => [route.path])
    const openKeys = ref([])

    // 从 store 获取菜单列表
    const menuList = computed(() => permissionStore.menus || [])
    const systemTitle = computed(() => userInfo.value.companyName || '库存管理')

    // 获取用户信息并初始化展开的菜单项
    onMounted(() => {
      const storedUserInfo = localStorage.getItem('userInfo')
      if (storedUserInfo) {
        try {
          userInfo.value = JSON.parse(storedUserInfo)
        } catch (error) {
          console.error('解析用户信息失败:', error)
          userInfo.value = {}
        }
      }

      // 默认展开所有有子菜单的项
      if (menuList.value && menuList.value.length > 0) {
        openKeys.value = menuList.value
          .filter(menu => menu.children && menu.children.length > 0)
          .map(menu => menu.permissionKey)
      }
    })

    const handleMenuSelect = ({ key }) => {
      router.push(key)
    }

    const handleOpenChange = (keys) => {
      openKeys.value = keys
    }

    const handleLogout = () => {
      Modal.confirm({
        title: '确认退出',
        content: '确定要退出登录吗？',
        onOk() {
          localStorage.removeItem('sessionId')
          localStorage.removeItem('userId')
          localStorage.removeItem('userInfo')
          permissionStore.resetPermissions()
          router.push('/login')
        }
      })
    }

    return {
      collapsed,
      selectedKeys,
      openKeys,
      userInfo,
      systemTitle,
      menuList,
      handleMenuSelect,
      handleOpenChange,
      handleLogout
    }
  }
}
</script>

<style scoped>
.layout {
  height: 100vh;
  overflow: hidden;
}

.layout :deep(.ant-layout-sider) {
  height: 100vh;
  overflow-y: auto;
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
}

.layout :deep(.ant-layout) {
  margin-left: 200px;
  height: 100vh;
  display: flex;
  flex-direction: column;
}

.layout :deep(.ant-layout.ant-layout-has-sider) {
  margin-left: 0;
}

.layout :deep(.ant-layout-sider-collapsed) + .ant-layout {
  margin-left: 80px;
}

.logo {
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.1);
  gap: 8px;
}

.logo-img {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

.logo h3 {
  color: white;
  margin: 0;
}

.version {
  text-align: center;
  color: rgba(255, 255, 255, 0.45);
  font-size: 11px;
  padding: 0 0 12px 0;
  background: rgba(255, 255, 255, 0.1);
}

.header {
  background: #fff;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 1px 4px rgba(0,21,41,.08);
  flex-shrink: 0;
}

.header-left {
  display: flex;
  align-items: center;
}

.trigger {
  font-size: 18px;
  line-height: 64px;
  padding: 0 24px;
  cursor: pointer;
  transition: color 0.3s;
}

.trigger:hover {
  color: #1890ff;
}

.breadcrumb {
  margin-left: 20px;
}

.header-right .ant-dropdown-link {
  color: #000;
  text-decoration: none;
}

.content {
  margin: 24px 16px;
  padding: 0;
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.content-wrapper {
  background: #fff;
  padding: 24px;
  border-radius: 4px;
  min-height: calc(100vh - 112px - 48px);
}
</style>
